// ResizeHandles.jsx - Reusable resize handles for SCADA components
import React from 'react';

const HANDLE_SIZE = 8;
const HANDLE_OFFSET = HANDLE_SIZE / 2;

const HANDLES = [
  { id: 'nw', cursor: 'nwse-resize', x: 0, y: 0 },
  { id: 'n', cursor: 'ns-resize', x: 0.5, y: 0 },
  { id: 'ne', cursor: 'nesw-resize', x: 1, y: 0 },
  { id: 'e', cursor: 'ew-resize', x: 1, y: 0.5 },
  { id: 'se', cursor: 'nwse-resize', x: 1, y: 1 },
  { id: 's', cursor: 'ns-resize', x: 0.5, y: 1 },
  { id: 'sw', cursor: 'nesw-resize', x: 0, y: 1 },
  { id: 'w', cursor: 'ew-resize', x: 0, y: 0.5 }
];

export default function ResizeHandles({
  x,
  y,
  width,
  height,
  onResizeStart,
  darkMode = false,
  rotation = 0
}) {
  const handleColor = darkMode ? '#60a5fa' : '#3b82f6';
  const handleBorderColor = darkMode ? '#1e3a5f' : '#1e40af';

  return (
    <g transform={`translate(${x}, ${y}) rotate(${rotation}, ${width / 2}, ${height / 2})`}>
      {/* Selection border */}
      <rect
        x={-1}
        y={-1}
        width={width + 2}
        height={height + 2}
        fill="none"
        stroke={handleColor}
        strokeWidth={1}
        strokeDasharray="4 2"
        pointerEvents="none"
      />

      {/* Resize handles */}
      {HANDLES.map(handle => {
        const hx = handle.x * width - HANDLE_OFFSET;
        const hy = handle.y * height - HANDLE_OFFSET;

        return (
          <rect
            key={handle.id}
            x={hx}
            y={hy}
            width={HANDLE_SIZE}
            height={HANDLE_SIZE}
            fill={handleColor}
            stroke={handleBorderColor}
            strokeWidth={1}
            rx={1}
            style={{ cursor: handle.cursor }}
            onMouseDown={(e) => {
              e.stopPropagation();
              onResizeStart(e, handle.id);
            }}
          />
        );
      })}
    </g>
  );
}

// Helper function to calculate new dimensions based on resize handle
export function calculateResize(handle, deltaX, deltaY, originalBounds, minSize = 20, snapToGrid = null) {
  let { x, y, width, height } = originalBounds;

  const snap = (value) => snapToGrid ? Math.round(value / snapToGrid) * snapToGrid : value;

  switch (handle) {
    case 'se':
      width = Math.max(minSize, snap(width + deltaX));
      height = Math.max(minSize, snap(height + deltaY));
      break;
    case 'e':
      width = Math.max(minSize, snap(width + deltaX));
      break;
    case 's':
      height = Math.max(minSize, snap(height + deltaY));
      break;
    case 'sw':
      const newWidthSW = Math.max(minSize, snap(width - deltaX));
      x = x + (width - newWidthSW);
      width = newWidthSW;
      height = Math.max(minSize, snap(height + deltaY));
      break;
    case 'w':
      const newWidthW = Math.max(minSize, snap(width - deltaX));
      x = x + (width - newWidthW);
      width = newWidthW;
      break;
    case 'nw':
      const newWidthNW = Math.max(minSize, snap(width - deltaX));
      const newHeightNW = Math.max(minSize, snap(height - deltaY));
      x = x + (width - newWidthNW);
      y = y + (height - newHeightNW);
      width = newWidthNW;
      height = newHeightNW;
      break;
    case 'n':
      const newHeightN = Math.max(minSize, snap(height - deltaY));
      y = y + (height - newHeightN);
      height = newHeightN;
      break;
    case 'ne':
      width = Math.max(minSize, snap(width + deltaX));
      const newHeightNE = Math.max(minSize, snap(height - deltaY));
      y = y + (height - newHeightNE);
      height = newHeightNE;
      break;
    default:
      break;
  }

  return { x, y, width, height };
}

// Helper for proportional resize (Shift key held)
export function calculateProportionalResize(handle, deltaX, deltaY, originalBounds, minSize = 20) {
  const { width, height } = originalBounds;
  const aspectRatio = width / height;

  let scale;
  if (['e', 'w'].includes(handle)) {
    scale = (width + (handle === 'e' ? deltaX : -deltaX)) / width;
  } else if (['n', 's'].includes(handle)) {
    scale = (height + (handle === 's' ? deltaY : -deltaY)) / height;
  } else {
    // Corner handles - use larger delta
    const scaleX = (width + (handle.includes('e') ? deltaX : -deltaX)) / width;
    const scaleY = (height + (handle.includes('s') ? deltaY : -deltaY)) / height;
    scale = Math.max(scaleX, scaleY);
  }

  const newWidth = Math.max(minSize, width * scale);
  const newHeight = Math.max(minSize, newWidth / aspectRatio);

  let newX = originalBounds.x;
  let newY = originalBounds.y;

  if (handle.includes('w')) {
    newX = originalBounds.x + (width - newWidth);
  }
  if (handle.includes('n')) {
    newY = originalBounds.y + (height - newHeight);
  }

  return { x: newX, y: newY, width: newWidth, height: newHeight };
}
